import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { Size as ButtonSize } from "@leafygreen-ui/button";
import { useToastContext } from "@evg-ui/lib/context/toast";
import { useTaskAnalytics } from "analytics";
import { ButtonDropdown, DropdownItem } from "components/ButtonDropdown";
import {
  QuarantineTestMutation,
  QuarantineTestMutationVariables,
  TaskQuery,
  TestResult,
  UnquarantineTestMutation,
  UnquarantineTestMutationVariables,
} from "gql/generated/types";
import { QUARANTINE_TEST, UNQUARANTINE_TEST } from "gql/mutations";
import { QuarantineDetails, QuarantineTestModal } from "./QuarantineTestModal";

interface ActionMenuProps {
  task: NonNullable<TaskQuery["task"]>;
  test: TestResult;
}

export const ActionMenu: React.FC<ActionMenuProps> = ({ task, test }) => {
  const { sendEvent } = useTaskAnalytics();
  const dispatchToast = useToastContext();
  const [open, setOpen] = useState(false);
  const [isQuarantineModalOpen, setIsQuarantineModalOpen] = useState(false);

  const [quarantineTest] = useMutation<
    QuarantineTestMutation,
    QuarantineTestMutationVariables
  >(QUARANTINE_TEST, {
    onCompleted: () => {
      dispatchToast.success("Successfully quarantined test.");
    },
    onError: (err) => {
      dispatchToast.error(
        `Error when attempting to quarantine test: ${err.message}`,
      );
    },
  });

  const [unquarantineTest] = useMutation<
    UnquarantineTestMutation,
    UnquarantineTestMutationVariables
  >(UNQUARANTINE_TEST, {
    onCompleted: () => {
      dispatchToast.success("Successfully unquarantined test.");
    },
    onError: (err) => {
      dispatchToast.error(
        `Error when attempting to unquarantine test: ${err.message}`,
      );
    },
  });

  const onQuarantineTest = ({
    existingJiraTicketsToLink,
    justification,
  }: QuarantineDetails) => {
    // Fall back to task.id since the field is nullable in the schema; for non-display tasks the two IDs are equal.
    const taskId = test.taskId ?? task.id;
    sendEvent({
      name: "Clicked quarantine test button",
      "test.name": test.testFile,
      "test.task_id": taskId,
    });
    quarantineTest({
      variables: {
        taskId,
        testName: test.testFile,
        existingJiraTicketsToLink,
        justification,
      },
    });
  };

  const onUnquarantineTest = () => {
    setOpen(false);
    const taskId = test.taskId ?? task.id;
    sendEvent({
      name: "Clicked unquarantine test button",
      "test.name": test.testFile,
      "test.task_id": taskId,
    });
    unquarantineTest({
      variables: { taskId, testName: test.testFile },
    });
  };

  let dropdownItems: React.ReactNode[];
  if (!task.testSelectionEnabled) {
    dropdownItems = [
      <DropdownItem key="disabled" disabled>
        Test selection did not run for this task, so its tests cannot be
        quarantined.
      </DropdownItem>,
    ];
  } else if (test.isManuallyQuarantined) {
    dropdownItems = [
      <DropdownItem
        key="unquarantine"
        data-testid="unquarantine-test"
        description="Allow this test to run in future task runs."
        onClick={onUnquarantineTest}
      >
        Unquarantine
      </DropdownItem>,
    ];
  } else {
    dropdownItems = [
      <DropdownItem
        key="quarantine"
        data-testid="quarantine-test"
        description="Quarantine test so that it does not run in future task runs."
        onClick={() => {
          setOpen(false);
          setIsQuarantineModalOpen(true);
        }}
      >
        Quarantine
      </DropdownItem>,
    ];
  }

  return (
    <>
      <ButtonDropdown
        dropdownItems={dropdownItems}
        open={open}
        setOpen={setOpen}
        size={ButtonSize.XSmall}
      />
      <QuarantineTestModal
        isOpen={isQuarantineModalOpen}
        onClose={() => setIsQuarantineModalOpen(false)}
        onConfirm={onQuarantineTest}
        requireJiraTickets={
          task.project?.testSelection?.quarantineJiraTicketsRequired ?? false
        }
        testName={test.testFile}
      />
    </>
  );
};
